import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

interface UseIPhoneTapReminderOptions {
  onTap?: () => void; // Called when user taps the reminder — used to reactivate wake lock
}

export const useIPhoneTapReminder = (options?: UseIPhoneTapReminderOptions) => {
  const [showReminder, setShowReminder] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(false);
  const isIPhoneRef = useRef(false);
  const onTapRef = useRef(options?.onTap);
  onTapRef.current = options?.onTap;

  useEffect(() => {
    // Detect iPhone and iPad
    isIPhoneRef.current = /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }, []);

  const startReminder = useCallback(() => {
    if (!isIPhoneRef.current || isActiveRef.current) return;

    isActiveRef.current = true;

    // Show first reminder at 30 seconds, then every 45 seconds
    // More aggressive than before because the screen blackout is a critical UX issue
    const showOnce = () => {
      setShowReminder(true);
      setTimeout(() => {
        setShowReminder(false);
      }, 4000);
    };

    // First reminder at 30s
    const firstTimeout = setTimeout(() => {
      showOnce();

      // Then repeat every 45s
      intervalRef.current = setInterval(showOnce, 45000);
    }, 30000);

    // Store the initial timeout so we can clear it
    intervalRef.current = firstTimeout as unknown as NodeJS.Timeout;
  }, []);

  const stopReminder = useCallback(() => {
    isActiveRef.current = false;
    setShowReminder(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      clearTimeout(intervalRef.current as unknown as NodeJS.Timeout);
      intervalRef.current = null;
    }
  }, []);

  const dismissReminder = useCallback(() => {
    setShowReminder(false);
    // When user taps the reminder, trigger wake lock reactivation
    if (onTapRef.current) {
      onTapRef.current();
    }
  }, []);

  useEffect(() => {
    return () => {
      stopReminder();
    };
  }, [stopReminder]);

  return useMemo(
    () => ({
      showReminder,
      startReminder,
      stopReminder,
      dismissReminder,
      isIPhone: isIPhoneRef.current,
    }),
    [showReminder, startReminder, stopReminder, dismissReminder],
  );
};
